import { computed } from '@angular/core';
import { AssetTypeSummary } from '@archimed/models';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { Asset } from '../../domain/models/asset.model';
import { ViewMode } from '../../domain/models/view-mode.enum';
import { HierarchyBuilderService } from '../../domain/services/hierarchy-builder.service';
import { TypeHierarchyBuilderService } from '../../domain/services/type-hierarchy-builder.service';
import { withDevtools } from './with-devtools.feature';

interface ExplorerStoreState {
  assets: Asset[];
  assetTypes: AssetTypeSummary[];
  selectedAssetId: string | null;
  isAssetsLoading: boolean;
  assetsError: string | null;
  isAssetTypesLoading: boolean;
  assetTypesError: string | null;
  nodeError: string | null;
  viewMode: ViewMode;
  loadedParentIds: Set<string>;
}

const initialState: ExplorerStoreState = {
  assets: [],
  assetTypes: [],
  selectedAssetId: null,
  isAssetsLoading: false,
  assetsError: null,
  isAssetTypesLoading: false,
  assetTypesError: null,
  nodeError: null,
  viewMode: ViewMode.INSTANCE,
  loadedParentIds: new Set<string>(),
};

const hierarchyBuilder = new HierarchyBuilderService();
const typeHierarchyBuilder = new TypeHierarchyBuilderService();

export const ExplorerStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withDevtools({ name: 'ExplorerStore' }),
  withComputed((store) => ({
    hierarchy: computed(() => hierarchyBuilder.buildFromFlat(store.assets())),

    typeHierarchy: computed(() =>
      typeHierarchyBuilder.buildFromEntityTypes(store.assetTypes()),
    ),

    hasAssets: computed(() => store.assets().length > 0),

    isAssetsLoaded: computed(() => store.assets().length > 0),
    isAssetTypesLoaded: computed(() => store.assetTypes().length > 0),

    isLoading: computed(
      () => store.isAssetsLoading() || store.isAssetTypesLoading(),
    ),
    error: computed(() => {
      return store.viewMode() === ViewMode.INSTANCE
        ? store.assetsError()
        : store.assetTypesError();
    }),
  })),
  withComputed((store) => ({
    selectedAsset: computed(() => {
      const selectedId = store.selectedAssetId();
      if (!selectedId) return null;
      return store.hierarchy().findById(selectedId) || null;
    }),
  })),
  withMethods((store) => ({
    loadAssets(assets: Asset[]): void {
      patchState(store, {
        assets,
        assetsError: null,
        loadedParentIds: new Set<string>(),
      });
    },

    mergeChildren(parentId: string, children: Asset[]): void {
      const existing = new Set(store.assets().map((a) => a.id));
      const fresh = children.filter((c) => !existing.has(c.id));
      const next = [...store.assets(), ...fresh];
      const loaded = new Set(store.loadedParentIds());
      loaded.add(parentId);
      patchState(store, { assets: next, loadedParentIds: loaded });
    },

    isChildrenLoaded(parentId: string): boolean {
      return store.loadedParentIds().has(parentId);
    },

    loadAssetTypes(assetTypes: AssetTypeSummary[]): void {
      patchState(store, { assetTypes });
    },

    setAssetsLoading(loading: boolean): void {
      patchState(store, { isAssetsLoading: loading });
    },

    setAssetsError(error: string | null): void {
      patchState(store, { assetsError: error, isAssetsLoading: false });
    },

    setAssetTypesLoading(loading: boolean): void {
      patchState(store, { isAssetTypesLoading: loading });
    },

    setAssetTypesError(error: string | null): void {
      patchState(store, { assetTypesError: error, isAssetTypesLoading: false });
    },

    setNodeError(nodeError: string | null): void {
      patchState(store, { nodeError });
    },

    updateAssetProperty(
      assetId: string,
      propertyName: string,
      value: unknown,
    ): void {
      const asset = store.hierarchy().findById(assetId);
      if (asset) {
        asset.updateProperty(propertyName, value);
        // Trigger reactivity by cloning the assets array reference
        patchState(store, { assets: [...store.assets()] });
      }
    },

    selectAsset(assetId: string | null): void {
      patchState(store, { selectedAssetId: assetId });
    },

    setViewMode(mode: ViewMode): void {
      patchState(store, { viewMode: mode, selectedAssetId: null });
    },

    clear(): void {
      patchState(store, initialState);
    },
  })),
);

export type ExplorerStoreType = InstanceType<typeof ExplorerStore>;
